/**
 * WHICH KEYBOARD THE PERSON IS ON, so a shortcut hint reads in their idiom.
 *
 * Vinnie, 10 Sep 2026: the hints showed Mac glyphs (⌘) to everyone — a Windows
 * person reading "⌘K" is being shown a key their keyboard does not have. The
 * behaviour already listens for both (metaKey || ctrlKey); only the HINT was
 * Mac-only. So the label follows the platform: ⌘ on a Mac, Ctrl elsewhere.
 *
 * Detection is best-effort and deliberately errs toward the Mac glyph when it
 * cannot tell (server render, tests, a locked-down browser) — that was the old
 * behaviour, so nothing regresses where the signal is absent.
 */
export function isApple(): boolean {
  if (typeof navigator === 'undefined') return true;
  // userAgentData.platform is the modern, non-deprecated signal; fall back to
  // the older two, which are still present everywhere that lacks it.
  const ua = navigator as Navigator & { userAgentData?: { platform?: string } };
  const hay = `${ua.userAgentData?.platform ?? ''} ${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`;
  return /Mac|iPhone|iPad|iPod/i.test(hay);
}

/** The modifier key on its own, in words or glyph: "⌘" on a Mac, "Ctrl" else. */
export const modKey = (): string => (isApple() ? '⌘' : 'Ctrl');

/**
 * A modifier+key combo in the OS idiom — `⌘K` / `Ctrl+K`, `⌘↵` / `Ctrl+Enter`.
 * On Windows/Linux the return arrow is spelled "Enter", the word on the key.
 */
export function combo(key: string): string {
  if (isApple()) return `⌘${key}`;
  return `Ctrl+${key === '↵' ? 'Enter' : key}`;
}
